import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { UserDetails } from '../types/User';
import { useEnhancedAuth } from './EnhancedAuthContext';
import { useAgencyDashboard } from '../hooks/useAgencyDashboard';
import { useAgentDashboard } from '../hooks/useAgentDashboard';
import { useCompanyDashboard } from '../hooks/useCompanyDashboard';
import { useManagerContext } from './ManagerContext';
import { useDocumentContext } from './DocumentContext';
import { useDocumentMap } from 'hooks/useDocumentMap';

interface DashboardContextType {
    agencyStats: ReturnType<typeof useAgencyDashboard>['agencyStats'];
    categoriesWithStats: ReturnType<typeof useAgencyDashboard>['categoriesWithStats'];
    companyStats: ReturnType<typeof useAgencyDashboard>['companyStats'];
    riskAgents: ReturnType<typeof useAgencyDashboard>['riskAgents'];
    agencyChartData: ReturnType<typeof useAgencyDashboard>['agencyChartData'];
    companyFFC: ReturnType<typeof useCompanyDashboard>['ffcStatus'];
    companyBBEE: ReturnType<typeof useCompanyDashboard>['beeStatus'];
    companyAuditDeadline: ReturnType<typeof useCompanyDashboard>['auditDeadline'];
    companyInfoOfficer: ReturnType<typeof useCompanyDashboard>['infoOfficer'];
    overallStats: any;
    ffcStatus: any;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

interface DashboardProviderProps {
    children: ReactNode;
    initialSelectedUser?: UserDetails | null;
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({
    children,
}) => {
    const {
        agencyUsers,
        companyDocuments,
        companyFieldValues,
        companyDocumentTypes,
        companyDocumentFields,
    } = useManagerContext();

    const { userDetails: normalizedEnhancedDetails } = useEnhancedAuth();

    const { documentTypes, documentFields, overallStats } = useDocumentContext();

    // Only required types/fields — matches the same filter in calculateOverallCompletion
    const requiredDocumentTypesCount = documentTypes.filter(dt => dt.required).length;
    const requiredDocumentFieldsCount = documentFields.filter(df => df.required).length;
    
    const map = useDocumentMap(normalizedEnhancedDetails);

    // Calculate agency dashboard stats
    const agencyDashboardData = useAgencyDashboard(
        agencyUsers,
        companyDocumentTypes.length,
        companyDocumentFields.length,
        requiredDocumentTypesCount,
        requiredDocumentFieldsCount,
        companyDocuments,
        companyFieldValues,
        map.categories
    );

    const isPaymentValid = normalizedEnhancedDetails?.agency?.isPaymentValid ?? false;

    const { ffcStatus } = useAgentDashboard(isPaymentValid ? normalizedEnhancedDetails?.id : undefined);

    const companyDashboardData = useCompanyDashboard(
        companyDocuments,
        companyFieldValues,
        isPaymentValid ? normalizedEnhancedDetails?.agency?.agencyId : undefined
    );


    const contextValue: DashboardContextType = useMemo(() => ({
        agencyStats: agencyDashboardData.agencyStats,
        categoriesWithStats: agencyDashboardData.categoriesWithStats,
        companyStats: agencyDashboardData.companyStats,
        riskAgents: agencyDashboardData.riskAgents,
        companyFFC: companyDashboardData.ffcStatus,
        companyBBEE: companyDashboardData.beeStatus,
        companyAuditDeadline: companyDashboardData.auditDeadline,
        companyInfoOfficer: companyDashboardData.infoOfficer,
        overallStats: overallStats,
        ffcStatus,
        agencyChartData: agencyDashboardData.agencyChartData
    }), [
        agencyDashboardData.agencyStats,
        agencyDashboardData.companyStats,
        agencyDashboardData.riskAgents,
        companyDashboardData.ffcStatus,
        companyDashboardData.beeStatus,
        companyDashboardData.auditDeadline,
        companyDashboardData.infoOfficer,
        overallStats,
        ffcStatus
    ]);

    return (
        <DashboardContext.Provider value={contextValue}>
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboardContext = () => {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error('useDocumentContext must be used within a DocumentProvider');
    }
    return context;
};
